import { useState, type ReactElement } from 'react';
import { Wrench } from 'lucide-react';
import type { MCPServerToolSummary } from '@kirin/api-client/contracts/mcp';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { MCPToolsDialog } from './MCPToolsDialog';

interface MCPToolsCellProps {
  serverName: string;
  tools: MCPServerToolSummary[] | null;
}

/**
 * Renders the tool count for an MCP inventory entry, clickable to open a
 * dialog listing each tool's name + description. Renders an em-dash when the
 * server hasn't reported tools (config-only entries that never connected).
 */
export function MCPToolsCell({
  serverName,
  tools,
}: MCPToolsCellProps): ReactElement {
  const [open, setOpen] = useState(false);

  if (!tools || tools.length === 0) {
    return <span className="text-muted-foreground">—</span>;
  }

  return (
    <>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            onClick={event => {
              // The tools cell sits on a row with click-to-expand, so
              // without this the click would open the dialog *and*
              // expand/collapse the row.
              event.stopPropagation();
              setOpen(true);
            }}
            className="inline-flex items-center gap-1.5 font-medium text-primary underline decoration-primary/40 underline-offset-2 hover:decoration-primary transition-colors cursor-pointer"
            aria-label={`Show ${tools.length} tools for ${serverName}`}
          >
            <Wrench className="h-3.5 w-3.5" />
            {tools.length}
          </button>
        </TooltipTrigger>
        <TooltipContent>
          <div className="space-y-1">
            <div>
              {tools.length} tool{tools.length === 1 ? '' : 's'} reported
            </div>
            <div className="text-xs text-muted-foreground">
              Click to see the tool list
            </div>
          </div>
        </TooltipContent>
      </Tooltip>
      <MCPToolsDialog
        open={open}
        onClose={() => setOpen(false)}
        serverName={serverName}
        tools={tools}
      />
    </>
  );
}
